import { useLayoutEffect, useRef, useState } from 'react';
import type { UploadHubListItem } from 'api/hooks/useUploadFiles';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import AddToEmailCampaignButton from './AddToEmailCampaignButton';
import AddToLobbyingMaterialButton from './AddToLobbyingMaterialButton';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';

export interface UploadHubOverflowAction {
  key: string;
  icon?: string;
  iconUrl?: string;
  label: string;
  onClick: () => void;
}

interface MoreActionsButtonProps {
  setIsDeleteOpen: (value: boolean) => void;
  setIsOpenAddEmail: (value: boolean) => void;
  setIsOpenAddLobbyingMaterial: (value: boolean) => void;
  file: UploadHubListItem | null;
  overflowActions?: UploadHubOverflowAction[];
}

const UploadHubPreviewPageMoreActions = (props: MoreActionsButtonProps) => {
  const { file, setIsDeleteOpen, setIsOpenAddEmail, setIsOpenAddLobbyingMaterial, overflowActions = [] } = props;
  const [isMoreActionsOpen, setIsMoreActionsOpen] = useState<boolean>(false);
  const [position, setPosition] = useState({ top: '0px', left: '0px', opacity: 0 });
  const moreActionsRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useOutsideElement(moreActionsRef, () => setIsMoreActionsOpen(false));
  useLayoutEffect(() => {
    if (!isMoreActionsOpen) return;
    const { left, bottom, width } = buttonRef.current?.getBoundingClientRect() ?? ({} as DOMRect);
    const { width: boxWidth } = boxRef.current?.getBoundingClientRect() ?? ({} as DOMRect);
    setPosition({ top: `${bottom + 8}px`, left: `${left + width - boxWidth}px`, opacity: 1 });
  }, [isMoreActionsOpen]);

  const isMobile = useIsMobile();

  return (
    <>
      <div className='ml-3' ref={moreActionsRef}>
        <button
          ref={buttonRef}
          type='button'
          aria-label='More actions'
          aria-expanded={isMoreActionsOpen}
          className={`general-button ${isMobile ? 'circle-button border circle-button-36' : 'specific-left-padding-18 specific-right-padding-13 specific-y-padding-5  rounded-pill border flex-centered justify-content-center'}`}
          onClick={() => setIsMoreActionsOpen(!isMoreActionsOpen)}
        >
          {isMobile ? (
            <span className='icon-three-dots light-60-text' />
          ) : (
            <span className='dropdown-item-element dropdown-item-element-10'>More</span>
          )}
        </button>
        {isMoreActionsOpen && file && (
          <div
            ref={boxRef}
            className='position-fixed box-shadow rounded pt-2 bg-main-white width-fit-content z-max'
            style={{ top: position.top, left: position.left, opacity: position.opacity }}
          >
            {overflowActions.map((action) => (
              <button
                key={action.key}
                className='w-100 d-flex align-items-center text-left general-button list-action-button px-3 py-2 upload-hub-mobile-more-actions'
                onClick={() => {
                  action.onClick();
                  setIsMoreActionsOpen(false);
                }}
              >
                {action.iconUrl ? (
                  <img src={action.iconUrl} alt='' style={{ width: '17px', marginRight: '14px' }} />
                ) : (
                  action.icon && (
                    <span
                      className={`political-updates-nav-icon ${action.icon} paragraph-p4 d-inline-block-centered specific-top-margin-1`}
                    />
                  )
                )}
                <span>{action.label}</span>
              </button>
            ))}
            <AddToEmailCampaignButton
              setIsMoreActionsOpen={setIsMoreActionsOpen}
              setIsOpenAddEmail={setIsOpenAddEmail}
            />
            <AddToLobbyingMaterialButton
              setIsMoreActionsOpen={setIsMoreActionsOpen}
              setIsOpenAddLobbyingMaterial={setIsOpenAddLobbyingMaterial}
            />
            <button
              className='w-100 d-flex align-items-center text-left general-button list-action-button specific-right-padding-28 pl-3 border-top mt-2  specific-y-padding-13 text-main-danger'
              onClick={() => {
                setIsMoreActionsOpen(false);
                setIsDeleteOpen(true);
              }}
            >
              <span
                className='political-updates-nav-icon specific-left-margin-3 icon-delete paragraph-p2 d-inline-block-centered specific-top-margin-1'
                style={{ marginRight: '-3.5px' }}
              />
              Delete file
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default UploadHubPreviewPageMoreActions;
